import React, { useState, useEffect } from "react";
import { Images, KeyRound, FileSignature, Megaphone, ScrollText, Upload, Plus, X, CheckCircle2, Phone, Send, FileText, Gauge, Link2, Download, Pencil, DoorOpen as DoorOpenIcon, CreditCard, Flag, Clock, UserCheck, Radio, Users2, FileUp, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import { ccInput, ccLabel, ccCard, ccBtnPink, ccBtnGhost, CCModal, ModalHeader, EmptyState } from "../care/ui";
import { MOCK_MEDIA, MOCK_PICKUPS, MOCK_DOCS, MOCK_COMMS, MOCK_LOGS, MOCK_APPLICATIONS } from "./opsData";
import Compliance from "./Compliance";
import Applications from "./Applications";
import SignaturePad from "./SignaturePad";
import Rooms from "./Rooms";
import Invoices from "./Invoices";
import { ComplaintsManager } from "./Complaints";
import TimeClock from "./TimeClock";
import HireCaregivers from "./HireCaregivers";
import LiveEvents from "./LiveEvents";
import UserDirectory from "../UserDirectory";

const now = () =>
  new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) +
  " " +
  new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

export default function DaycareOps({ children: childList, allowedTabs, onAlert, currentUserName, currentUserEmail, facilityName, allChildren, rooms, invoices, ws }) {
  const isLive = true; // site live for real users — operations modules start clean
  const [tab, setTab] = useState(() => {
    const t = sessionStorage.getItem("nt_ops_tab");
    if (t) sessionStorage.removeItem("nt_ops_tab");
    return t || "media";
  });
  const [posts, setPosts] = useState(isLive ? [] : MOCK_MEDIA);
  const [pickups, setPickups] = useState(isLive ? {} : MOCK_PICKUPS);
  const [docs, setDocs] = useState(isLive ? [] : MOCK_DOCS);
  const [comms, setComms] = useState(isLive ? [] : MOCK_COMMS);
  const [logs, setLogs] = useState(isLive ? [] : MOCK_LOGS);
  const [applications, setApplications] = useState(isLive ? [] : MOCK_APPLICATIONS);

  const addLog = (action, targetTable, details) =>
    setLogs((prev) => [{ id: `log_${Date.now()}`, user: currentUserName || "Michael Winston", action, targetTable, details, createdAt: now() }, ...prev]);

  const opsRole = allowedTabs ? (allowedTabs.includes("comms") ? "admin" : allowedTabs.includes("ratios") ? "teacher" : "parent") : "admin";

  const ctx = {
    posts, setPosts, pickups, setPickups, docs, setDocs, comms, setComms, logs, setLogs, addLog, childList,
    applications, setApplications, onAlert, currentUserName, currentUserEmail, facilityName: facilityName || "NestTrack Facility",
    allChildren: allChildren || childList,
    rooms: rooms || [],
    invoices: invoices || [],
    ws,
    role: opsRole,
  };

  const TABS = [
    { key: "media", label: "Media Feed", icon: Images },
    { key: "pickup", label: "Pickup List", icon: KeyRound },
    { key: "rooms", label: "Rooms", icon: DoorOpenIcon },
    { key: "billing", label: "Billing & Invoices", icon: CreditCard },
    { key: "documents", label: "E-Documents", icon: FileSignature },
    { key: "applications", label: "Applications", icon: FileText },
    { key: "complaints", label: "Complaints", icon: Flag },
    { key: "timeclock", label: "Time Clock", icon: Clock },
    { key: "hire", label: "Hire Caregivers", icon: UserCheck },
    { key: "users", label: "Users", icon: Users2 },
    { key: "live", label: "Live", icon: Radio },
    { key: "ratios", label: "Ratios & Compliance", icon: Gauge },
    { key: "comms", label: "Communications", icon: Megaphone },
    { key: "logs", label: "Audit Logs", icon: ScrollText },
  ];

  const visibleTabs = allowedTabs ? TABS.filter((t) => allowedTabs.includes(t.key)) : TABS;
  const activeTab = visibleTabs.some((t) => t.key === tab) ? tab : visibleTabs[0].key;

  return (
    <section className={`${ccCard} p-6 animate-fade-up`} data-testid="daycare-ops">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <h3 className="text-base font-extrabold text-[#374151] font-heading dark:text-slate-100 flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-full bg-[#FFF2D1] flex items-center justify-center">
            <ScrollText className="w-4 h-4 text-[#D9A93E]" />
          </span>
          Daycare Operations
        </h3>
        <div className="flex gap-1.5 flex-nowrap overflow-x-auto w-full pb-1 sm:flex-wrap sm:overflow-visible sm:w-auto" data-testid="ops-tabs">
          {visibleTabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`ops-tab-${t.key}`}
              className={`flex shrink-0 items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-bold transition-all duration-200 ${
                tab === t.key
                  ? "bg-[#F8C8DC] text-[#374151] shadow-sm"
                  : "text-[#9CA3AF] hover:text-[#374151] hover:bg-[#FFF9F2] dark:hover:bg-[#273244]"
              }`}
            >
              <t.icon className="w-3.5 h-3.5" />
              {t.label}
            </button>
          ))}
        </div>
      </div>
      {activeTab === "media" && <MediaFeed ctx={ctx} />}
      {activeTab === "pickup" && <PickupList ctx={ctx} />}
      {activeTab === "rooms" && <Rooms ctx={ctx} />}
      {activeTab === "billing" && <Invoices ctx={ctx} />}
      {activeTab === "documents" && (
        <>
          <Documents ctx={ctx} />
          <PolicyUploads ctx={ctx} />
        </>
      )}
      {activeTab === "applications" && <Applications ctx={ctx} role={allowedTabs ? (allowedTabs.includes("comms") ? "admin" : allowedTabs.includes("ratios") ? "teacher" : "parent") : "admin"} />}
      {activeTab === "complaints" && <ComplaintsManager />}
      {activeTab === "timeclock" && <TimeClock ctx={ctx} />}
      {activeTab === "hire" && <HireCaregivers />}
      {activeTab === "users" && <UserDirectory scope="facility" currentUserEmail={currentUserEmail} />}
      {activeTab === "live" && <LiveEvents ctx={ctx} />}
      {activeTab === "ratios" && <Compliance ctx={ctx} />}
      {activeTab === "comms" && <CommCenter ctx={ctx} />}
      {activeTab === "logs" && <AuditLogs ctx={ctx} />}
    </section>
  );
}

// ---------- Media Feed ----------
const MediaFeed = ({ ctx }) => {
  const [caption, setCaption] = useState("");
  const [photo, setPhoto] = useState(null);
  const [tags, setTags] = useState([]);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setPhoto(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    if (!photo) {
      toast.error("Attach a photo first.");
      return;
    }
    ctx.setPosts((prev) => [
      { id: `med_${Date.now()}`, mediaUrl: photo, mediaType: "photo", caption, childTags: tags, uploader: "Michael Winston", createdAt: now() },
      ...prev,
    ]);
    ctx.addLog("CREATE", "media_posts", `Uploaded 1 photo to parent feed (${tags.length} child tag${tags.length === 1 ? "" : "s"})`);
    toast.success("Posted to the parent feed.");
    setCaption("");
    setPhoto(null);
    setTags([]);
  };

  const childName = (id) => {
    const c = ctx.childList.find((x) => x.id === id) || ctx.allChildren.find((x) => x.id === id);
    return c ? `${c.firstName} ${c.lastName}` : id;
  };

  return (
    <div className="space-y-5" data-testid="media-feed">
      {ctx.role !== "parent" && (
      <form onSubmit={submit} className="p-4 bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl space-y-3 dark:bg-[#273244] dark:border-[#374457]">
        <div className="flex items-center gap-3 flex-wrap">
          <label className={`${ccBtnGhost} !py-2 cursor-pointer inline-flex items-center gap-2`} data-testid="media-upload-btn">
            <Upload className="w-4 h-4 text-[#7CC3E5]" />
            {photo ? "Change Photo" : "Upload Photo"}
            <input type="file" accept="image/*" onChange={handleFile} className="hidden" />
          </label>
          {photo && <img src={photo} alt="preview" className="w-10 h-10 rounded-xl object-cover border border-[#FCE6E9]" data-testid="media-upload-preview" />}
          <input
            type="text"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="Write a caption for parents..."
            className={`${ccInput} flex-1 min-w-[200px]`}
            data-testid="media-caption-input"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] font-bold text-[#9CA3AF] uppercase tracking-wide">Tag children:</span>
          {ctx.childList.map((c) => (
            <button
              type="button"
              key={c.id}
              onClick={() => setTags((prev) => (prev.includes(c.id) ? prev.filter((t) => t !== c.id) : [...prev, c.id]))}
              data-testid={`media-tag-${c.id}`}
              className={`px-3 py-1.5 rounded-full text-[11px] font-bold border transition-all ${
                tags.includes(c.id) ? "bg-[#B3E0F2] border-[#9ACCE0] text-[#374151]" : "bg-white border-[#FCE6E9] text-[#9CA3AF] dark:bg-[#232D3D] dark:border-[#374457]"
              }`}
            >
              {c.firstName} {c.lastName}
            </button>
          ))}
          <button type="submit" className={`${ccBtnPink} !py-2 ml-auto`} data-testid="media-post-submit">
            Post
          </button>
        </div>
      </form>
      )}

      {ctx.posts.length === 0 ? (
        <EmptyState text="No media posts yet. Share the first moment with families." testId="media-empty" />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {ctx.posts.map((p) => (
            <div key={p.id} className="rounded-2xl overflow-hidden border border-[#FCE6E9] bg-white dark:bg-[#232D3D] dark:border-[#374457]" data-testid={`media-post-${p.id}`}>
              <img src={p.mediaUrl} alt={p.caption} className="w-full h-44 object-cover" />
              <div className="p-3.5">
                <p className="text-xs font-semibold text-[#374151] dark:text-slate-200">{p.caption || "Shared moment"}</p>
                {p.childTags.length > 0 && (
                  <div className="flex gap-1 mt-2 flex-wrap">
                    {p.childTags.map((t) => (
                      <span key={t} className="px-2 py-0.5 bg-[#E6F2FA] text-[#3D7E9E] text-[10px] font-bold rounded-full">
                        {childName(t)}
                      </span>
                    ))}
                  </div>
                )}
                <p className="text-[10px] text-[#9CA3AF] mt-2">
                  {p.uploader} · {p.createdAt}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------- Authorized Pickup List ----------
const PickupList = ({ ctx }) => {
  const [form, setForm] = useState({});

  const setField = (childId, field, value) => setForm((prev) => ({ ...prev, [childId]: { ...prev[childId], [field]: value } }));

  const addContact = (childId) => {
    const f = form[childId] || {};
    if (!f.contactName?.trim() || !f.phone?.trim()) {
      toast.error("Name and phone are required.");
      return;
    }
    ctx.setPickups((prev) => ({
      ...prev,
      [childId]: [...(prev[childId] || []), { id: `pk_${Date.now()}`, contactName: f.contactName, relationship: f.relationship || "Guardian", phone: f.phone, isActive: true }],
    }));
    ctx.addLog("CREATE", "pickup_contacts", `Added authorized pickup: ${f.contactName}`);
    toast.success("Pickup contact added.");
    setForm((prev) => ({ ...prev, [childId]: {} }));
  };

  const toggleActive = (childId, id) => {
    ctx.setPickups((prev) => ({
      ...prev,
      [childId]: prev[childId].map((c) => (c.id === id ? { ...c, isActive: !c.isActive } : c)),
    }));
    ctx.addLog("UPDATE", "pickup_contacts", `Toggled pickup authorization status`);
  };

  const removeContact = (childId, id, name) => {
    ctx.setPickups((prev) => ({ ...prev, [childId]: prev[childId].filter((c) => c.id !== id) }));
    ctx.addLog("DELETE", "pickup_contacts", `Removed authorized pickup: ${name}`);
    toast("Contact removed.");
  };

  return (
    <div className="space-y-4" data-testid="pickup-list">
      {ctx.childList.length === 0 ? (
        <EmptyState text="No enrolled children. Enroll a child to manage pickup contacts." testId="pickup-empty" />
      ) : (
        ctx.childList.map((c) => {
          const contacts = ctx.pickups[c.id] || [];
          const f = form[c.id] || {};
          return (
            <div key={c.id} className="p-4 bg-[#FFF9F2] border border-[#E6F2FA] rounded-2xl dark:bg-[#273244] dark:border-[#374457]" data-testid={`pickup-child-${c.id}`}>
              <p className="text-sm font-extrabold font-heading text-[#374151] dark:text-slate-100 mb-3">
                {c.firstName} {c.lastName} — Authorized Pickup
              </p>
              <div className="space-y-2 mb-3">
                {contacts.length === 0 && <p className="text-xs text-[#9CA3AF]">No authorized contacts yet.</p>}
                {contacts.map((ct) => (
                  <div key={ct.id} className="flex items-center justify-between gap-2 flex-wrap bg-white border border-[#FCE6E9] rounded-xl px-3.5 py-2.5 dark:bg-[#232D3D] dark:border-[#374457]" data-testid={`pickup-contact-${ct.id}`}>
                    <div className="flex items-center gap-3">
                      <span className={`w-2 h-2 rounded-full ${ct.isActive ? "bg-[#7CC98F]" : "bg-[#D1D5DB]"}`} />
                      <div>
                        <p className="text-xs font-extrabold text-[#374151] dark:text-slate-100">
                          {ct.contactName} <span className="font-semibold text-[#9CA3AF]">· {ct.relationship}</span>
                        </p>
                        <p className="text-[11px] text-[#9CA3AF] flex items-center gap-1">
                          <Phone className="w-3 h-3" /> {ct.phone}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => toggleActive(c.id, ct.id)} className="px-2.5 py-1 text-[10px] font-bold rounded-full bg-[#E6F2FA] text-[#3D7E9E] hover:bg-[#D6EBF7] transition-colors" data-testid={`pickup-toggle-${ct.id}`}>
                        {ct.isActive ? "Deactivate" : "Activate"}
                      </button>
                      <button onClick={() => removeContact(c.id, ct.id, ct.contactName)} className="p-1 text-[#C9CDD4] hover:text-rose-400 transition-colors" data-testid={`pickup-remove-${ct.id}`}>
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 flex-wrap">
                <input type="text" placeholder="Full name" value={f.contactName || ""} onChange={(e) => setField(c.id, "contactName", e.target.value)} className={`${ccInput} !py-2 flex-1 min-w-[130px]`} data-testid={`pickup-name-${c.id}`} />
                <input type="text" placeholder="Relationship" value={f.relationship || ""} onChange={(e) => setField(c.id, "relationship", e.target.value)} className={`${ccInput} !py-2 w-32`} data-testid={`pickup-rel-${c.id}`} />
                <input type="tel" placeholder="Phone" value={f.phone || ""} onChange={(e) => setField(c.id, "phone", e.target.value)} className={`${ccInput} !py-2 w-36`} data-testid={`pickup-phone-${c.id}`} />
                <button onClick={() => addContact(c.id)} className={`${ccBtnPink} !py-2`} data-testid={`pickup-add-${c.id}`}>
                  <Plus className="w-4 h-4 inline -mt-0.5" /> Add
                </button>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

// ---------- E-Signature Documents (canvas signature + PDF engine + dispatch links) ----------
// ---------- Policy & Job Documents (admin upload -> master review) ----------
const PolicyUploads = ({ ctx }) => {
  const B = process.env.REACT_APP_BACKEND_URL;
  const [items, setItems] = useState([]);
  const [me, setMe] = useState(null);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("policy");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = () =>
    fetch(`${B}/api/workspace/documents`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setItems(d.documents || []))
      .catch(() => {});
  useEffect(() => {
    load();
    fetch(`${B}/api/auth/me`, { credentials: "include" }).then((r) => (r.ok ? r.json() : null)).then(setMe).catch(() => {});
  }, []);
  const isMaster = me?.role === "master";

  const upload = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Choose a file first."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("title", title);
      fd.append("category", category);
      fd.append("file", file);
      const res = await fetch(`${B}/api/workspace/documents`, { method: "POST", credentials: "include", body: fd });
      const body = await res.json();
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Upload failed");
      toast.success("Document uploaded for review.");
      ctx.addLog("INSERT", "ws_documents", `Uploaded for review: ${title}`);
      setTitle(""); setCategory("policy"); setFile(null);
      load();
    } catch (err) { toast.error(err.message); } finally { setUploading(false); }
  };

  const review = async (id, action) => {
    try {
      const res = await fetch(`${B}/api/workspace/documents/${id}/review`, {
        method: "PATCH", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action }),
      });
      if (!res.ok) throw new Error("Review failed");
      toast.success(action === "reviewed" ? "Marked as reviewed." : "Sent back for changes.");
      load();
    } catch (err) { toast.error(err.message); }
  };

  const STATUS = {
    pending_review: { label: "Pending Review", cls: "bg-[#FFF2D1] border-[#F5E3B3] text-[#A08244]" },
    reviewed: { label: "Reviewed", cls: "bg-[#E2F4E1] border-[#C4E5C2] text-[#2F6031]" },
    needs_changes: { label: "Needs Changes", cls: "bg-rose-50 border-rose-200 text-rose-500" },
  };

  return (
    <div className="mt-8 pt-6 border-t border-[#FCE6E9] dark:border-[#374457]" data-testid="policy-uploads">
      <h4 className="text-sm font-extrabold text-[#374151] font-heading dark:text-slate-100 flex items-center gap-2 mb-1">
        <span className="w-7 h-7 rounded-full bg-[#E6F2FA] flex items-center justify-center">
          <FileUp className="w-3.5 h-3.5 text-[#3D7E9E]" />
        </span>
        Policy & Job Documents — Upload for Review
      </h4>
      <p className="text-[11px] text-[#9CA3AF] font-medium mb-4">New policies, job descriptions, procedures — uploaded files are stored securely and queued for admin review.</p>
      <form onSubmit={upload} className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-4">
        <div className="sm:col-span-2">
          <label className={ccLabel}>Document Title</label>
          <input required value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. 2026 Illness Policy" className={ccInput} data-testid="policy-doc-title" />
        </div>
        <div>
          <label className={ccLabel}>Category</label>
          <select value={category} onChange={(e) => setCategory(e.target.value)} className={ccInput} data-testid="policy-doc-category">
            <option value="policy">Policy</option>
            <option value="job_description">Job Description</option>
            <option value="procedure">Procedure</option>
            <option value="form">Form</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div>
          <label className={ccLabel}>File</label>
          <input required type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.zip" onChange={(e) => setFile(e.target.files?.[0] || null)} className={ccInput} data-testid="policy-doc-file" />
        </div>
        <div className="sm:col-span-4">
          <button type="submit" disabled={uploading} className={`${ccBtnPink} disabled:opacity-50`} data-testid="policy-doc-submit">
            {uploading ? "Uploading..." : "Upload for Review"}
          </button>
        </div>
      </form>
      {items.length === 0 ? (
        <EmptyState message="No documents uploaded yet." />
      ) : (
        <div className="space-y-2" data-testid="policy-doc-list">
          {items.map((d) => (
            <div key={d.id} className="p-3 bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl flex items-center justify-between gap-3 flex-wrap dark:bg-[#273244] dark:border-[#374457]" data-testid={`policy-doc-${d.id}`}>
              <div className="min-w-0">
                <p className="text-xs font-extrabold text-[#374151] dark:text-slate-100 truncate">{d.title}</p>
                <p className="text-[10px] text-[#9CA3AF] font-medium">{d.filename} · {d.category.replace(/_/g, " ")} · by {d.uploaded_by}</p>
                {d.review_note && <p className="text-[10px] text-[#A08244] font-medium mt-0.5">Note: {d.review_note}</p>}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`px-2.5 py-1 text-[9px] font-extrabold uppercase rounded-full border ${(STATUS[d.status] || STATUS.pending_review).cls}`} data-testid={`policy-doc-status-${d.id}`}>
                  {(STATUS[d.status] || STATUS.pending_review).label}
                </span>
                <a href={`${B}/api/workspace/documents/${d.id}/file`} target="_blank" rel="noreferrer" className="w-8 h-8 rounded-full bg-white border border-[#FCE6E9] flex items-center justify-center text-[#3D7E9E] hover:bg-[#E6F2FA] transition-colors" title="Download" data-testid={`policy-doc-download-${d.id}`}>
                  <Download className="w-3.5 h-3.5" />
                </a>
                {isMaster && d.status === "pending_review" && (
                  <>
                    <button onClick={() => review(d.id, "reviewed")} className="w-8 h-8 rounded-full bg-[#E2F4E1] border border-[#C4E5C2] flex items-center justify-center text-[#2F6031] hover:bg-[#C4E5C2] transition-colors" title="Mark reviewed" data-testid={`policy-doc-approve-${d.id}`}>
                      <ClipboardCheck className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => review(d.id, "needs_changes")} className="w-8 h-8 rounded-full bg-rose-50 border border-rose-200 flex items-center justify-center text-rose-500 hover:bg-rose-100 transition-colors" title="Needs changes" data-testid={`policy-doc-reject-${d.id}`}>
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Documents = ({ ctx }) => {
  const [signing, setSigning] = useState(null);
  const [sigName, setSigName] = useState("");
  const [sigData, setSigData] = useState(null);

  const childName = (id) => {
    const c = ctx.childList.find((x) => x.id === id) || ctx.allChildren.find((x) => x.id === id);
    return c ? `${c.firstName} ${c.lastName}` : id;
  };

  const sign = (e) => {
    e.preventDefault();
    if (!sigData) {
      toast.error("Please draw your signature on the pad.");
      return;
    }
    ctx.setDocs((prev) =>
      prev.map((d) => (d.id === signing.id ? { ...d, isSigned: true, signedBy: sigName, signedAt: now(), signatureData: sigData } : d))
    );
    ctx.addLog("UPDATE", "documents", `E-signed document: ${signing.title} (canvas signature)`);
    toast.success("Document signed — PDF ready to download.");
    setSigning(null);
    setSigName("");
    setSigData(null);
  };

  const downloadPdf = (d) => {
    const doc = new jsPDF();
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text("NestTrack", 20, 20);
    doc.setFontSize(13);
    doc.text(d.title, 20, 32);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(`Facility: ${ctx.facilityName}`, 20, 44);
    doc.text(`Child: ${d.childName || childName(d.childId)}`, 20, 51);
    const body = d.body ||
      `This document authorizes ${ctx.facilityName} to proceed as described in the parent handbook for ${d.childName || childName(d.childId)}. By signing, the parent/guardian acknowledges and agrees to the terms herein.`;
    doc.text(doc.splitTextToSize(body, 170), 20, 62);
    if (d.signatureData) doc.addImage(d.signatureData, "PNG", 20, 120, 70, 32);
    doc.setFontSize(9);
    doc.text(`Signed by: ${d.signedBy || ""}`, 20, 160);
    doc.text(`Signed at: ${d.signedAt || ""}`, 20, 166);
    doc.text("Document ID: " + d.id + " · Generated by NestTrack PDF Engine", 20, 172);
    doc.save(`${d.title.replace(/[^a-z0-9]+/gi, "_")}.pdf`);
    ctx.addLog("VIEW", "documents", `PDF downloaded: ${d.title}`);
  };

  const dispatchDoc = (d) => {
    const link = `https://nesttrackapp.com/documents/review/${d.id}`;
    navigator.clipboard?.writeText(link).catch(() => {});
    ctx.setComms((prev) => [
      { id: `com_${Date.now()}`, channel: "email", recipientType: "individual", destination: "parent@example.com", message: `Document review link dispatched: ${link}`, sentAt: now(), status: "sent" },
      ...prev,
    ]);
    ctx.addLog("DISPATCH_COMMUNICATION", "communications", `email → review link for "${d.title}"`);
    toast.success("Trackable review link copied & dispatch logged.");
  };

  return (
    <div className="space-y-3" data-testid="documents-list">
      {ctx.docs.map((d) => (
        <div key={d.id} className="p-4 bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl dark:bg-[#273244] dark:border-[#374457]" data-testid={`doc-row-${d.id}`}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <span className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${d.isSigned ? "bg-[#E2F4E1]" : "bg-[#FFF2D1]"}`}>
                <FileSignature className={`w-5 h-5 ${d.isSigned ? "text-[#7CC98F]" : "text-[#D9A93E]"}`} />
              </span>
              <div>
                <p className="text-sm font-extrabold text-[#374151] font-heading dark:text-slate-100">
                  {d.title}
                  {d.auto && <span className="ml-2 px-2 py-0.5 bg-[#E6F2FA] text-[#3D7E9E] text-[9px] font-extrabold uppercase rounded-full">Auto-generated</span>}
                </p>
                <p className="text-[11px] text-[#9CA3AF]">
                  For: {d.childName || childName(d.childId)}
                  {d.isSigned && ` · Signed by ${d.signedBy} on ${d.signedAt}`}
                </p>
              </div>
            </div>
            {d.isSigned ? (
              <span className="px-3 py-1.5 bg-[#E2F4E1] border border-[#C4E5C2] text-[#2F6031] text-[10px] font-extrabold uppercase rounded-full flex items-center gap-1" data-testid={`doc-signed-${d.id}`}>
                <CheckCircle2 className="w-3.5 h-3.5" /> Signed
              </span>
            ) : (
              <button onClick={() => setSigning(d)} className={`${ccBtnPink} !py-2`} data-testid={`doc-sign-${d.id}`}>
                Review & Sign
              </button>
            )}
          </div>
          <div className="flex gap-2 mt-3">
            <button onClick={() => dispatchDoc(d)} className="px-3 py-1.5 bg-white border border-[#B3E0F2] hover:bg-[#E6F2FA] text-[#3D7E9E] rounded-full text-[11px] font-bold transition-colors flex items-center gap-1 dark:bg-[#232D3D]" data-testid={`doc-dispatch-${d.id}`}>
              <Link2 className="w-3 h-3" /> Dispatch Review Link
            </button>
            {d.isSigned && (
              <button onClick={() => downloadPdf(d)} className="px-3 py-1.5 bg-white border border-[#C4E5C2] hover:bg-[#E2F4E1] text-[#2F6031] rounded-full text-[11px] font-bold transition-colors flex items-center gap-1 dark:bg-[#232D3D]" data-testid={`doc-pdf-${d.id}`}>
                <Download className="w-3 h-3" /> Download PDF
              </button>
            )}
          </div>
        </div>
      ))}

      {signing && (
        <CCModal onClose={() => setSigning(null)} testId="sign-modal" wide>
          <ModalHeader title="Review & E-Sign" icon={FileSignature} onClose={() => setSigning(null)} closeTestId="sign-modal-close" />
          <p className="text-sm font-extrabold text-[#374151] font-heading mb-2 dark:text-slate-100">{signing.title}</p>
          <div className="p-4 bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl mb-5 max-h-36 overflow-y-auto dark:bg-[#273244] dark:border-[#374457]" data-testid="sign-doc-body">
            <p className="text-xs text-[#6B7280] leading-relaxed dark:text-slate-300">
              {signing.body ||
                `This document authorizes ${ctx.facilityName} to proceed as described in the parent handbook for ${signing.childName || childName(signing.childId)}. By signing, the parent/guardian acknowledges and agrees to the terms herein.`}
            </p>
          </div>
          <form onSubmit={sign} className="space-y-4">
            <div>
              <label className={ccLabel}>Full legal name</label>
              <input required type="text" value={sigName} onChange={(e) => setSigName(e.target.value)} className={ccInput} placeholder="e.g. Sarah Jenkins" data-testid="sign-name-input" />
            </div>
            <div>
              <label className={ccLabel}>Signature</label>
              <SignaturePad onChange={setSigData} />
            </div>
            <div className="pt-4 flex justify-end gap-3 border-t border-[#FCE6E9] dark:border-[#374457]">
              <button type="button" onClick={() => setSigning(null)} className={ccBtnGhost} data-testid="sign-cancel">
                Cancel
              </button>
              <button type="submit" className={ccBtnPink} data-testid="sign-submit">
                Sign & Execute
              </button>
            </div>
          </form>
        </CCModal>
      )}
    </div>
  );
};

// ---------- Communication Center (mock dispatch, fully logged) ----------
const CommCenter = ({ ctx }) => {
  const [form, setForm] = useState({ recipientType: "all_parents", destination: "", channel: "email", message: "" });

  const dispatch = (e) => {
    e.preventDefault();
    const destination = form.destination || (form.recipientType === "all_parents" ? "all-parents@sunshineacademy.com" : "");
    if (!destination || !form.message.trim()) {
      toast.error("Destination and message are required.");
      return;
    }
    const entry = { id: `com_${Date.now()}`, ...form, destination, sentAt: now(), status: "sent" };
    ctx.setComms((prev) => [entry, ...prev]);
    ctx.addLog("DISPATCH_COMMUNICATION", "communications", `${form.channel} → ${destination}`);
    toast.success(`Demo dispatch logged — ${form.channel.toUpperCase()} to ${destination}.`);
    setForm({ recipientType: "all_parents", destination: "", channel: "email", message: "" });
  };

  const channelChip = { sms: "bg-[#E2F4E1] text-[#2F6031] border-[#C4E5C2]", call: "bg-[#FFF2D1] text-[#7A5A17] border-[#F5E3B3]", email: "bg-[#E6F2FA] text-[#3D7E9E] border-[#B3E0F2]" };

  return (
    <div className="space-y-5" data-testid="comm-center">
      <form onSubmit={dispatch} className="p-4 bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl space-y-3 dark:bg-[#273244] dark:border-[#374457]">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className={ccLabel}>Recipients</label>
            <select value={form.recipientType} onChange={(e) => setForm({ ...form, recipientType: e.target.value })} className={ccInput} data-testid="comm-recipient-type">
              <option value="all_parents">All Parents</option>
              <option value="all_staff">All Staff</option>
              <option value="individual">Individual</option>
            </select>
          </div>
          <div>
            <label className={ccLabel}>Channel</label>
            <select value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })} className={ccInput} data-testid="comm-channel">
              <option value="email">Email</option>
              <option value="sms">SMS</option>
              <option value="call">Voice Call</option>
            </select>
          </div>
          <div>
            <label className={ccLabel}>Destination</label>
            <input type="text" value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value })} placeholder={form.channel === "email" ? "email@example.com" : "+1 (555) 000-0000"} className={ccInput} data-testid="comm-destination" />
          </div>
        </div>
        <textarea rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Type your announcement..." className={ccInput} data-testid="comm-message" />
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-[10px] text-[#9CA3AF] font-medium">Demo mode — dispatches are logged, no real SMS/email is sent. Live sending needs Twilio/Resend keys.</p>
          <button type="submit" className={ccBtnPink} data-testid="comm-dispatch">
            <Send className="w-4 h-4 inline mr-1 -mt-0.5" /> Dispatch
          </button>
        </div>
      </form>

      <div className="space-y-2" data-testid="comm-log">
        {ctx.comms.map((c) => (
          <div key={c.id} className="p-3.5 bg-white border border-[#FCE6E9] rounded-2xl flex items-start justify-between gap-3 flex-wrap dark:bg-[#232D3D] dark:border-[#374457]" data-testid={`comm-item-${c.id}`}>
            <div className="min-w-0">
              <p className="text-xs font-bold text-[#374151] dark:text-slate-100 truncate">{c.destination}</p>
              <p className="text-xs text-[#6B7280] mt-0.5 dark:text-slate-300">{c.message}</p>
              <p className="text-[10px] text-[#9CA3AF] mt-1">{c.sentAt}</p>
            </div>
            <span className={`px-2.5 py-1 border text-[10px] font-extrabold uppercase rounded-full ${channelChip[c.channel]}`}>{c.channel}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------- Audit Logs ----------
const AuditLogs = ({ ctx }) => {
  const [editingLog, setEditingLog] = useState(null);
  const [form, setForm] = useState({ user: "", action: "", targetTable: "", details: "" });

  const openEdit = (l) => {
    setForm({ user: l.user, action: l.action, targetTable: l.targetTable, details: l.details });
    setEditingLog(l);
  };

  const saveEdit = (e) => {
    e.preventDefault();
    ctx.setLogs((prev) => prev.map((l) => (l.id === editingLog.id ? { ...l, ...form, edited: true } : l)));
    ctx.addLog("UPDATE", "system_logs", `Edited audit log entry ${editingLog.id} (${editingLog.action})`);
    toast.success("Log entry updated.");
    setEditingLog(null);
  };

  const actionChip = {
    CREATE: "bg-[#E2F4E1] text-[#2F6031] border-[#C4E5C2]",
    UPDATE: "bg-[#E6F2FA] text-[#3D7E9E] border-[#B3E0F2]",
    DELETE: "bg-rose-50 text-rose-500 border-rose-200",
    VIEW: "bg-[#FFF9F2] text-[#9CA3AF] border-[#FCE6E9]",
    DISPATCH_COMMUNICATION: "bg-[#FCE6E9] text-[#B2607F] border-[#F8C8DC]",
  };

  return (
    <div className="overflow-x-auto" data-testid="audit-logs">
      <table className="w-full text-sm min-w-[640px]">
        <thead>
          <tr className="text-left text-[10px] font-extrabold uppercase tracking-wider text-[#9CA3AF] border-b border-[#FCE6E9] dark:border-[#374457]">
            <th className="pb-2.5 pr-4">Action</th>
            <th className="pb-2.5 pr-4">User</th>
            <th className="pb-2.5 pr-4">Target</th>
            <th className="pb-2.5 pr-4">Details</th>
            <th className="pb-2.5 pr-4">Timestamp</th>
            <th className="pb-2.5">Edit</th>
          </tr>
        </thead>
        <tbody>
          {ctx.logs.length === 0 && (
            <tr>
              <td colSpan={6} className="py-8 text-center">
                <p className="text-xs text-[#9CA3AF] font-medium" data-testid="audit-logs-empty">No audit entries yet — actions across the operations modules will be logged here.</p>
              </td>
            </tr>
          )}
          {ctx.logs.map((l) => (
            <tr key={l.id} className="border-b border-[#FCE6E9]/60 dark:border-[#374457]/60" data-testid={`log-row-${l.id}`}>
              <td className="py-3 pr-4">
                <span className={`px-2.5 py-1 border text-[10px] font-extrabold uppercase rounded-full whitespace-nowrap ${actionChip[l.action] || actionChip.VIEW}`}>{l.action}</span>
              </td>
              <td className="py-3 pr-4 text-xs font-bold text-[#374151] dark:text-slate-200 whitespace-nowrap">{l.user}</td>
              <td className="py-3 pr-4 text-xs font-mono text-[#5CB3DC] whitespace-nowrap">{l.targetTable}</td>
              <td className="py-3 pr-4 text-xs text-[#6B7280] dark:text-slate-300">
                {l.details}
                {l.edited && <span className="ml-1.5 text-[9px] font-bold text-[#D9A93E]">edited</span>}
              </td>
              <td className="py-3 pr-4 text-[11px] text-[#9CA3AF] whitespace-nowrap">{l.createdAt}</td>
              <td className="py-3">
                <button onClick={() => openEdit(l)} data-testid={`log-edit-btn-${l.id}`} title="Edit log entry" className="w-7 h-7 rounded-full flex items-center justify-center text-[#9CA3AF] hover:text-[#5CB3DC] hover:bg-[#E6F2FA] transition-colors">
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {editingLog && (
        <CCModal onClose={() => setEditingLog(null)} testId="log-edit-modal">
          <ModalHeader title="Edit Audit Log Entry" icon={Pencil} onClose={() => setEditingLog(null)} closeTestId="log-edit-close" />
          <form onSubmit={saveEdit} className="space-y-3.5" data-testid="log-edit-form">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={ccLabel}>User</label>
                <input value={form.user} onChange={(e) => setForm({ ...form, user: e.target.value })} className={ccInput} data-testid="log-edit-user" />
              </div>
              <div>
                <label className={ccLabel}>Action</label>
                <input value={form.action} onChange={(e) => setForm({ ...form, action: e.target.value.toUpperCase() })} className={ccInput} data-testid="log-edit-action" />
              </div>
            </div>
            <div>
              <label className={ccLabel}>Target Table</label>
              <input value={form.targetTable} onChange={(e) => setForm({ ...form, targetTable: e.target.value })} className={ccInput} data-testid="log-edit-target" />
            </div>
            <div>
              <label className={ccLabel}>Details</label>
              <textarea value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} className={`${ccInput} min-h-[70px]`} data-testid="log-edit-details" />
            </div>
            <button type="submit" className={ccBtnPink} data-testid="log-edit-save">Save Changes</button>
          </form>
        </CCModal>
      )}
    </div>
  );
};
